import { SubscriptionTypeEnum } from "../lib/enums"
import { useUserContext } from "./use-user"

// Subscription tier hierarchy levels
const TIER_LEVELS = {
  [SubscriptionTypeEnum.Free]: 0,
  [SubscriptionTypeEnum.Basic]: 1,
  [SubscriptionTypeEnum.Premium]: 2,
  [SubscriptionTypeEnum.Enterprise]: 3,
}

// Subscription limits
export const SUBSCRIPTION_LIMITS = {
  [SubscriptionTypeEnum.Free]: {
    maxInventoryItems: 0,
    barcodeGeneration: false,
    listings: false,
  },
  [SubscriptionTypeEnum.Basic]: {
    maxInventoryItems: 200,
    barcodeGeneration: false,
    listings: true,
  },
  [SubscriptionTypeEnum.Premium]: {
    maxInventoryItems: 1000,
    barcodeGeneration: true,
    listings: true,
  },
  [SubscriptionTypeEnum.Enterprise]: {
    maxInventoryItems: Infinity,
    barcodeGeneration: true,
    listings: true,
  },
}

export const useSubscriptionTooling = () => {
  const { subscription } = useUserContext()

  /**
   * Check if user has access to a specific tier
   * Returns true if user's tier is equal to or higher than the required tier
   */
  const hasAccess = (required: SubscriptionTypeEnum): boolean => {
    if (!subscription) return false

    const userLevel = TIER_LEVELS[subscription] || 0
    const requiredLevel = TIER_LEVELS[required] || 0

    return userLevel >= requiredLevel
  }

  /**
   * Get the current subscription limits for the user
   */
  const getSubscriptionLimits = () => {
    //If the user doesn't have a subscription, return Free tier limits
    if (!subscription) {
      return SUBSCRIPTION_LIMITS[SubscriptionTypeEnum.Free]
    }

    return SUBSCRIPTION_LIMITS[subscription] || SUBSCRIPTION_LIMITS[SubscriptionTypeEnum.Free]
  }

  /**
   * Check if user can add more inventory items
   */
  const canAddInventoryItem = (currentCount: number): boolean => {
    const limits = getSubscriptionLimits()
    return currentCount < limits.maxInventoryItems
  }

  /**
   * Check if barcode generation is available
   */
  const hasBarcodeAccess = (): boolean => {
    const limits = getSubscriptionLimits()
    return limits.barcodeGeneration
  }

  /**
   * Check if listings feature is available
   */
  const hasListingsAccess = (): boolean => {
    const limits = getSubscriptionLimits()
    return limits.listings
  }

  return {
    hasAccess,
    getSubscriptionLimits,
    canAddInventoryItem,
    hasBarcodeAccess,
    hasListingsAccess,
    subscription
  }
}
